import { MarkerF, OverlayView, OverlayViewF } from '@react-google-maps/api';
import { useTranslation } from 'next-i18next';
import * as React from 'react';
import { TbX } from 'react-icons/tb';
import { Property } from '../../../Shared/Services/Properties/Models/Property';
import { State as QueryFilters } from '../Hooks/useQueryFilters';
import { createCoordFromKey } from '../Utils/createCoordFromKey';
import { createPropertyPrice } from '../Utils/createPropertyPrice';
import { SRPCard } from './SRPCard';

interface Props {
  state: QueryFilters;
  clusters: Record<string, Property | Property[]>;
  mapRef: React.MutableRefObject<google.maps.Map | null>;
}

export function SRPMapMarkers({ state, clusters, mapRef }: Props) {
  const { t } = useTranslation(['auctions', 'search']);
  const [selected, setSelected] = React.useState<string | null>(null);

  const elements = Object.keys(clusters).flatMap((clusterK) => {
    const cluster = clusters[clusterK];
    const coords = createCoordFromKey(clusterK);

    if (
      !mapRef.current?.getBounds()?.contains(coords) &&
      clusterK !== selected
    ) {
      return [];
    }

    if (!state.zoom || state.zoom < 13) {
      return (
        <MarkerF
          key={clusterK}
          position={coords}
          icon="/assets/images/pin.svg"
          onClick={() => {
            mapRef.current?.setCenter(coords);
            mapRef.current?.setZoom(13);
          }}
        />
      );
    }

    if (Array.isArray(cluster)) {
      return (
        <OverlayViewF
          key={clusterK}
          mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
          position={coords}
        >
          <button
            className="rounded-full bg-white text-zinc-800 font-semibold py-1.5 px-2 drop-shadow text-xs"
            onClick={() => setSelected(clusterK)}
          >
            {cluster.length} {t('map.units', { ns: 'search' })}
          </button>

          {selected === clusterK && (
            <OverlayViewF
              zIndex={10000}
              position={coords}
              mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
              getPixelPositionOffset={(width, height) => ({
                x: -(width / 2),
                y: 40,
              })}
            >
              <div className="p-5 h-96 space-y-5 rounded-md bg-white overflow-y-auto">
                {cluster.map((item) => (
                  <div
                    key={item.id}
                    className="w-80 relative"
                    onTouchStart={(e) => e.stopPropagation()}
                  >
                    <SRPCard mini={true} data={item} />
                    <button
                      onClick={() => setSelected(null)}
                      className="absolute text-xl rounded-lg h-6 w-6 inline-flex items-center justify-center top-4 left-4 text-white bg-zinc-700"
                      aria-label="Close"
                    >
                      <TbX />
                    </button>
                  </div>
                ))}
              </div>
            </OverlayViewF>
          )}
        </OverlayViewF>
      );
    } else {
      return (
        <OverlayViewF
          key={clusterK}
          mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
          position={coords}
        >
          <button
            className="rounded-full bg-zinc-600 py-1.5 px-2 drop-shadow text-xs text-white"
            onClick={() => setSelected(clusterK)}
          >
            {createPropertyPrice(cluster, t)}
          </button>

          {selected === clusterK && (
            <OverlayViewF
              zIndex={10000}
              position={coords}
              mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
              getPixelPositionOffset={(width, height) => ({
                x: -(width / 2),
                y: 40,
              })}
            >
              <div className="w-80 relative">
                <SRPCard mini={true} data={cluster} />
                <button
                  onClick={() => setSelected(null)}
                  className="absolute text-xl rounded-lg h-6 w-6 inline-flex items-center justify-center top-4 left-4 text-white bg-zinc-700"
                  aria-label="Close"
                >
                  <TbX />
                </button>
              </div>
            </OverlayViewF>
          )}
        </OverlayViewF>
      );
    }
  });

  return <div>{elements}</div>;
}
